// Read-only views of content and config for the store, persistence and view models (24.2:
// nothing outside sim reads config.ts directly; they go through the api).
import { config, type Config } from '../config';
import { registry } from '../content';
import type { BuildingLimit } from '../core/registry';
import type { BuildingTypeId, JobId, PolicyId, Resource } from '../core/types';
import type {
  Decision,
  GameState,
  Policies,
  PreviewLine,
  ValidationProblem,
} from '../core/types';
import { readContext } from './context';

export interface Catalog {
  jobs: readonly {
    id: JobId;
    label: string;
    heavy: boolean;
    requiresBuilding: BuildingTypeId | null;
    /** Section 10: tool multiplier, or null for jobs that use no tool (stream M3a). */
    toolFactor: number | null;
  }[];
  buildings: readonly {
    id: BuildingTypeId;
    label: string;
    buildPoints: number;
    materials: Partial<Record<Resource, number>>;
    /** Section 9 (stream M3a): limit, beds, plots, stock capacity added, fire material. */
    limit: BuildingLimit;
    beds: number;
    plots: number;
    capacity: Partial<Record<Resource, number>>;
    material: 'wood' | 'stone';
  }[];
  /** Section 9 building rules for the Build tab (stream M3a). */
  building: {
    upkeepFraction: number;
    upkeepEverySeasons: number;
    cancelRefund: number;
    decayPerSeason: number;
    fullEffectCondition: number;
  };
  /** Section 10 tool rules for the Tools panel (stream M3a). */
  tools: {
    wearPerUse: number;
    noToolmakerWarnSeasons: number;
    noToolmakerDecaySeasons: number;
  };
  policies: readonly {
    id: PolicyId;
    label: string;
    group: string;
    options: readonly unknown[] | null;
  }[];
  populationPresets: readonly number[];
  populationMin: number;
  populationMax: number;
  presets: Config['newGame']['presets'];
  seasonsPerYear: number;
  adultAtSeasons: number;
  elderAtSeasons: number;
  /** Below this fraction of need a person counts as short (Section 6, D-09). */
  shortThreshold: number;
  /** Section 7: base spoilage per season by resource (a stock that spoils is worth warning
   * about when it is over capacity, where it spoils at 3x). */
  spoilage: Readonly<Record<Resource, number>>;
  overCapacitySpoilageMul: number;
}

let cached: Catalog | null = null;

export function catalog(): Catalog {
  cached ??= {
    jobs: Object.values(registry.jobs).map((j) => ({
      id: j.id,
      label: j.label,
      heavy: j.heavy,
      requiresBuilding: j.requiresBuilding,
      toolFactor: j.toolFactor,
    })),
    buildings: Object.values(registry.buildings).map((b) => ({
      id: b.id,
      label: b.label,
      buildPoints: b.buildPoints,
      materials: b.materials,
      limit: b.limit,
      beds: b.beds,
      plots: b.plots,
      capacity: b.capacity,
      material: b.material,
    })),
    building: {
      upkeepFraction: config.building.upkeepFraction,
      upkeepEverySeasons: config.building.upkeepEverySeasons,
      cancelRefund: config.building.cancelRefund,
      decayPerSeason: config.building.decayPerSeason,
      fullEffectCondition: config.building.fullEffectCondition,
    },
    tools: {
      wearPerUse: config.tools.wearPerUse,
      noToolmakerWarnSeasons: config.tools.noToolmakerWarnSeasons,
      noToolmakerDecaySeasons: config.tools.noToolmakerDecaySeasons,
    },
    policies: Object.values(registry.policies).map((p) => ({
      id: p.id,
      label: p.label,
      group: p.group,
      options: p.options,
    })),
    populationPresets: config.newGame.populationPresets,
    populationMin: config.newGame.populationMin,
    populationMax: config.newGame.populationMax,
    presets: config.newGame.presets,
    seasonsPerYear: config.people.seasonsPerYear,
    adultAtSeasons: config.people.adultAtSeasons,
    elderAtSeasons: config.people.elderAtSeasons,
    shortThreshold: config.needs.shortThreshold,
    spoilage: config.resources.spoilage,
    overCapacitySpoilageMul: config.resources.overCapacitySpoilageMul,
  };
  return cached;
}

/** The full config, for the save envelope's configSnapshot (ADR-0008). */
export function configSnapshot(): Config {
  return structuredClone(config);
}

// ---- Stream F (Section 18 policy screen): the "affects next season" preview line.
export interface PolicyPreview {
  /** Lines from the policy def's `affects` hook, keyed for ui/text/en.ts. */
  lines: readonly PreviewLine[];
  /** One-time trust change for everyone when switching from the standing value (<= 0). */
  changeCost: number;
  /** Why this option cannot be chosen now (execution locked), or null. */
  problem: ValidationProblem | null;
}

/**
 * Section 18: preview a policy option against the state with the draft's other policy changes
 * applied, through the def's `affects`, `changeCost` and `validate` hooks.
 */
export function policyPreview<K extends PolicyId>(
  state: GameState,
  draft: Pick<Decision, 'policies'>,
  policyId: K,
  option: Policies[K],
  cfg: Config = config,
): PolicyPreview {
  const policies = { ...state.policies, ...draft.policies, [policyId]: option };
  const ctx = readContext({ ...state, policies }, cfg);
  const def = ctx.registry.policies[policyId] as unknown as {
    affects: (c: typeof ctx, v: Policies[K]) => readonly PreviewLine[];
    changeCost: (from: Policies[K], to: Policies[K]) => number;
    validate: (c: typeof ctx, v: Policies[K]) => ValidationProblem | null;
  };
  return {
    lines: def.affects(ctx, option),
    changeCost: def.changeCost(state.policies[policyId], option),
    problem: def.validate(ctx, option),
  };
}
